import React, { lazy, Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { getAvailablePages, sessionId, SiteConfigManager } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { Loader } from '@atlas/ui';

import StaticPage from 'common/StaticPage/StaticPage';
import useBodyClassName from 'components/Layout/useBodyClassName';
import { defaultRoutes } from 'components/MainRouter/constants';
import { SMARTICO_STORE_PATH } from 'components/Pages/Store/constants';
import NotFound from 'components/Sportsbook/NotFound';
import { renderSportsbookProvider } from 'components/Sportsbook/providerRegistry';
import { usePostHog } from 'hooks/usePostHog';
import useOauth from 'src/components/MainRouter/useOauth';

import { availableGeneralRoutes } from './constants';

// Code-split so the heavy `@atlas/components` chain the store pulls in is only
// loaded when the store is actually visited.
const StorePage = lazy(
  () => import(/* webpackChunkName: "StorePage" */ 'components/Pages/Store/StorePage'),
);

const SPORTSBOOK_ROUTE_TYPES = [COMPONENT_TYPES.SPORT_LIVE, COMPONENT_TYPES.SPORT_PREMATCH];

const MainRouter = () => {
  useBodyClassName();

  const history = useHistory();

  const routes = getAvailablePages(
    availableGeneralRoutes,
    SiteConfigManager.getExtraConfig('components'),
  );

  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const routeToRedirect = routes[0].path || clientProfilePath;

  useOauth();

  const { init, login } = usePostHog();
  useEffect(() => {
    init().then(() => {
      login(sessionId);
    });
  }, [init, login]);

  return (
    <Suspense fallback={<Loader className="layout-loader" />}>
      <Switch>
        {/*
          Smartico Store renders as a standalone full page at its canonical
          `/account/store` URL (matches akira). It is matched before the
          config-driven routes so the exact match wins over the Client Profile
          `/account` route — React Router v5 <Switch> resolves by order.
        */}
        <Route component={StorePage} exact path={SMARTICO_STORE_PATH} />

        {routes.map(route => {
          const isSportsbookRoute = SPORTSBOOK_ROUTE_TYPES.includes(route.type);
          const providerNode = isSportsbookRoute ? renderSportsbookProvider() : null;

          if (providerNode) {
            return (
              <Route key={route.type} path={route.path}>
                {providerNode}
              </Route>
            );
          }

          return <Route component={route.component} key={route.type} path={route.path} />;
        })}

        <Route component={StaticPage} path={defaultRoutes.staticPage} />

        <Redirect exact from="/" to={`${routeToRedirect}${history.location.search}`} />

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
};

export default MainRouter;
